import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { AlertCircle, Loader2 } from 'lucide-react';

const LoginPage = () => {
  const { login, error: authError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(email, password);
    
    if (!result.success) {
      setError(result.error);
    }
    
    setLoading(false);
  };

  // Check for license expiry error
  if (authError === 'License Expired, Contact DWEB') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F5FA] p-4">
        <Card className="w-full max-w-md border-[#C85A40] bg-[#C85A40]/5">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-[#C85A40]/10 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-[#C85A40]" />
            </div>
            <CardTitle className="text-2xl text-[#C85A40]">License Expired</CardTitle>
            <CardDescription className="text-[#5C635F] mt-2">
              Your software license has expired. Please contact DWEB to renew your license and continue using the system.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-[#5C635F]">
              Contact: <strong>DWEB Support</strong>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Login form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="mb-8">
            <img 
              src="https://acornsschools.com/wp-content/uploads/2024/06/Plain-Acorns-1536x587.png" 
              alt="Acorns Schools" 
              className="h-16 object-contain"
            />
          </div>

          <Card className="border-[#D8D9E8]">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-[#1C1F1D]">Welcome back</CardTitle>
              <CardDescription className="text-[#5C635F]">
                Sign in to access the Fee Management System
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="bg-[#C85A40]/10 border border-[#C85A40]/20 text-[#C85A40] px-4 py-3 rounded-lg flex items-center gap-2 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#1C1F1D]">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@acorns.edu"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="border-[#D8D9E8] focus:border-[#3E3F96] focus:ring-[#3E3F96]/20"
                    data-testid="login-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#1C1F1D]">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="border-[#D8D9E8] focus:border-[#3E3F96] focus:ring-[#3E3F96]/20"
                    data-testid="login-password-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#3E3F96] hover:bg-[#2D2E7A] text-white"
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign in'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-[#5C635F] mt-6">
            Acorns School Fee Management System
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div 
        className="hidden lg:flex flex-1 bg-cover bg-center relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1763582516354-e417d2902207?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxzY2hvb2wlMjBsaWJyYXJ5JTIwYXJjaGl0ZWN0dXJlJTIwb3JnYW5pY3xlbnwwfHx8fDE3NzUyMjIzMDd8MA&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="absolute inset-0 bg-[#3E3F96]/60 flex items-center justify-center p-12">
          <div className="text-white text-center max-w-lg">
            <h2 className="text-4xl font-bold mb-4">
              Streamlined Fee Management
            </h2>
            <p className="text-lg opacity-90">
              Complete financial control for your educational institution. Track fees, manage payments, and generate reports with ease.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
